import { FormControl, FormLabel } from "@mui/joy";

import "./SiteBadgeCode.css";
import { QRCode } from "react-qrcode-logo";
import { Avatar } from "primereact/avatar";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { getFileBlobUrlS3Proxy } from "../../service/s3Service";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { useToast } from "../../context/ToastContext";

interface inputPrams {
  company_logo_s3_key?: string;
  client_id?: string;
  site_id?: string;
  site_name?: string;
  contact_details?: string[];
}

interface RecipientInfo {
  email: string;
  fullName: string;
}

export default function SiteBadgeCode(params: inputPrams) {
  const toast = useToast();
  let { company_logo_s3_key, client_id, site_id, site_name, contact_details } = params;
  const [logoBlobURL, setLogoBlobURL] = useState<string | null>(null);
  const elementRef = useRef<HTMLImageElement>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [contacts, setRecipients] = useState<RecipientInfo[]>([{ email: "", fullName: "" }]);

  const link = `https://pwa.digitalsafetyfiles.com/home/${site_id}`;

  const htmlToImageConvert = () => {
    console.log("elementRef == ", elementRef);
    if (elementRef?.current) {
      toPng(elementRef.current, { cacheBust: false })
        .then(dataUrl => {
          console.log("dataUrl == ", dataUrl);
          const link = document.createElement("a");
          link.download = `${site_name}.png`;
          link.href = dataUrl;
          link.click();
        })
        .catch(err => {
          console.log("htmlToImageConvert error ", err);
        });
    }
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(link);
    toast("success", "Link copied to clipboard");
  };

  const handleShareClick = () => {
    setShowShareDialog(true);
  };

  const handleAddRecipient = () => {
    setRecipients([...contacts, { email: "", fullName: "" }]);
  };

  const handleRemoveRecipient = (index: number) => {
    if (contacts.length > 1) {
      const newRecipients = contacts.filter((_, i) => i !== index);
      setRecipients(newRecipients);
    }
  };

  const handleRecipientChange = (index: number, field: keyof RecipientInfo, value: string) => {
    const newRecipients = [...contacts];
    newRecipients[index][field] = value;
    setRecipients(newRecipients);
  };

  const handleShareSubmit = () => {
    // Validate that all fields are filled
    const validRecipients = contacts.filter(
      contact => contact.email.trim() !== "" && contact.fullName.trim() !== ""
    );

    if (validRecipients.length === 0) {
      toast("error", "Please fill in at least one complete contact");
      return;
    }

    // Here you can implement the sharing logic
    console.log("Sharing with contacts:", validRecipients);

    toast("success", `Site badge shared with ${validRecipients.length} contact(s)`);

    setShowShareDialog(false);
    setRecipients([{ email: "", fullName: "" }]); // Reset form
  };

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await getFileBlobUrlS3Proxy(company_logo_s3_key!);
        setLogoBlobURL(response);
      } catch (error) {
        console.error("Error fetching logo:", error);
      }
    };

    fetchImage();
  }, [params.company_logo_s3_key]);

  const shareDialogFooter = (
    <div>
      <Button label="Share" icon="pi pi-share-alt" onClick={handleShareSubmit} />
    </div>
  );

  return (
    <FormControl>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginBottom: 20 }}>
        {/* <Button
          type="button"
          icon="pi pi-share-alt"
          label="Share"
          className="p-button-outlined"
          style={{ color: "#ffc409" }}
          size="small"
          onClick={handleShareClick}
        /> */}
        <Button
          type="button"
          icon="pi pi-copy"
          label="Copy Link"
          className="p-button-outlined"
          size="small"
          style={{ color: "#2dd36f" }}
          onClick={copyLinkToClipboard}
        />
        <Button
          type="button"
          icon="pi pi-download"
          label="Download"
          className="p-button-outlined"
          size="small"
          onClick={htmlToImageConvert}
        />
      </div>

      <Dialog
        header="Share Site Badge"
        visible={showShareDialog}
        style={{ width: "80%" }}
        footer={shareDialogFooter}
        onHide={() => setShowShareDialog(false)}
        modal
      >
        <div style={{ padding: "20px 0" }}>
          <p>
            Please enter email address and the names of all the recipients you want to email the
            Site Poster to:
          </p>

          {contacts.map((contact, index) => (
            <div key={index} style={{ marginBottom: "15px", padding: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                {contacts.length > 1 && (
                  <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-text p-button-sm"
                    onClick={() => handleRemoveRecipient(index)}
                  />
                )}
              </div>

              <div style={{ display: "flex", gap: "15px" }}>
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor={`fullName-${index}`}
                    style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}
                  >
                    Name:
                  </label>
                  <InputText
                    id={`fullName-${index}`}
                    value={contact.fullName}
                    onChange={e => handleRecipientChange(index, "fullName", e.target.value)}
                    placeholder="Enter Name"
                    style={{ width: "100%" }}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <label
                    htmlFor={`email-${index}`}
                    style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}
                  >
                    Email Address:
                  </label>
                  <IconField iconPosition="left">
                    <InputIcon className="pi pi-at"> </InputIcon>
                    <InputText
                      id={`email-${index}`}
                      type="email"
                      value={contact.email}
                      onChange={e => handleRecipientChange(index, "email", e.target.value)}
                      placeholder="Enter email address"
                      style={{ width: "100%" }}
                    />
                  </IconField>
                </div>
              </div>
            </div>
          ))}

          <Button
            label="Add Another Recipient"
            icon="pi pi-plus"
            className="p-button-outlined"
            onClick={handleAddRecipient}
            style={{ marginTop: "10px" }}
          />
        </div>
      </Dialog>

      <div ref={elementRef} style={{ display: "flex", width: 850, backgroundColor: "white" }}>
        <div style={{ flex: 0.25 }}>
          <img
            src="/site_badge_side.png"
            alt="Site Badge Side"
            style={{ width: "95%", height: "100%" }}
          />
        </div>
        <div style={{ flex: 11.5, padding: "15px 20px" }}>
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ flex: 1 }}>
              {logoBlobURL && (
                <img src={logoBlobURL} alt="Site Badge Side" style={{ width: 150, height: 150, margin: '0 25px' }} />
              )}
            </div>
            <div style={{ flex: 10, textAlign: "center" }}>
              <label className="QrLabelHeading">Welcome to</label>
              <br />
              <br />
              <label className="QrLabelHeading">{site_name}</label>
            </div>
            <div style={{ flex: 1 }}>
              <img
                src="/DSF_Logo-(High).png"
                alt="Site Badge Side"
                style={{ width: 200 }}
              />
            </div>
          </div>
          <br />
          <label className="QrLabelHeading">Site Attendance & Safety Information</label>
          <br />
          <label className="QrLabelHeadingDanger">BEFORE ENTERING</label>
          <br />
          <label className="QrLabelHeading">Scan the QR Code Below to:</label>
          <ul>
            <li className="QrLabelDetails">
              Register your attendance (Employees, Contractors, Visitors)
            </li>
            <li className="QrLabelDetails">Access the Site Induction</li>
            <li className="QrLabelDetails">View Emergency Recipient Details</li>
            <li className="QrLabelDetails">Explore the Digital Safety File</li>
          </ul>
          <br />
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ flex: 3 }}>
              <img
                src="/site_badge_scan_code.png"
                alt="Site Badge Side"
                className="QrCodeScanImage"
              />
            </div>
            <div style={{ flex: 6, textAlign: "center" }}>
              <QRCode style={{ height: 280, width: 280 }} value={link} qrStyle="dots" />
            </div>
            <div style={{ flex: 3 }}>
              <img
                src="/site_badge_scan_code.png"
                alt="Site Badge Side"
                className="QrCodeScanImage"
              />
            </div>
          </div>
          <br />
          <label className="QrLabelSafeDetails">
            For Immediate Assistance, Recipient Site Management:
          </label>
          <br />
          {contact_details &&
            contact_details.map((detail, index) => (
              <>
                <label key={index} className="QrLabelSafeDetails">
                  {detail}
                </label>
                <br />
              </>
            ))}
          <br />
          <label className="QrLabelSafe">Stay Safe and Informed!</label>
        </div>
        <div style={{ flex: 0.25 }}>
          <img
            src="/site_badge_side.png"
            alt="Site Badge Side"
            style={{ width: "95%", height: "100%", float: "right" }}
          />
        </div>
      </div>
    </FormControl>
  );
}
